import { useQuery, useMutation, useQueryClient } from 'react-query'
import { quoteService } from '../services'
import {
  IAssociateMedicalPlansDTO,
  ICreateQuoteDTO,
  IGroupQueryParams,
  IMedicalPlanCreateDTO,
  IMedicalPlanDTO,
  IPatchQuoteDTO,
  IPastMedicalPlanCreateDTO,
  ICreateGroupDTO,
  ICensusUpdateDTO,
  IFileType,
  ISoldQuoteDTO, ICreateAgentOfRecordDTO, ICreateAgencyDTO, IAttachAgencyDTO
} from '../services/quote/dto'
import { useToaster } from 'react-angle-dashboard-components'

enum EQueryKeys {
  quoteGroups = 'quoteGroups',
  generalAgencies = 'generalAgencies',
  quotes = 'quotes',
  vendorChoices = 'vendorChoices',
  medicalPlans = 'medicalPlans',
  censusInfo = 'censusInfo',
  censusRisk = 'censusRisk',
  pastMedicalPlans = 'pastMedicalPlans',
  uploadedFiles = 'uploadedFiles',
  triggerRateCalc = 'triggerRateCalc'
}

export const useQuote = (id: string | undefined | null) =>
  useQuery(
    [EQueryKeys.quotes, id],
    () => (id ? quoteService.getQuoteById(id) : undefined),
    {
      enabled: !!id
    }
  )

export const useCreateQuote = () =>
  useMutation((data: ICreateQuoteDTO) => quoteService.createQuote(data))

export const useCreateAgentOfRecord = () =>
  useMutation((data: ICreateAgentOfRecordDTO) => quoteService.createAgentOfRecord(data))

export const useCreateAgency = () =>
  useMutation((data: ICreateAgencyDTO) => quoteService.createGeneralAgency(data))

export const useAttachAgency = () =>
  useMutation(({ quoteId, generalAgencyId }: IAttachAgencyDTO) =>
    quoteService.associateGeneralAgency(quoteId, generalAgencyId))

export const useUpdateQuote = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ quoteId, data }: { quoteId: string; data: Partial<IPatchQuoteDTO> }) =>
      quoteService.updateQuote(quoteId, data),
    {
      onSuccess: (d, vars) => {
        queryClient.invalidateQueries([EQueryKeys.quotes, vars.quoteId])
      }
    }
  )
}

export const useAssociateMedicalPlans = () =>
  useMutation(
    ({ data, quoteId }: { data: IAssociateMedicalPlansDTO; quoteId: string }) =>
      quoteService.associateMedicalPlansToQuote(quoteId, data)
  )

export const useCensusInfo = (quoteId: string | null) =>
  useQuery(
    [EQueryKeys.censusInfo, quoteId],
    () => (quoteId ? quoteService.getCensusInfo(quoteId) : undefined),
    {
      enabled: !!quoteId
    }
  )

export const useAssociateCensus = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ data, quoteId }: { data: ICensusUpdateDTO; quoteId: string }) =>
      quoteService.associateCensusToQuote(quoteId, data),
    {
      onSuccess: (data, { quoteId }) =>
        queryClient.invalidateQueries([EQueryKeys.censusInfo, quoteId])
    }
  )
}

export const useTriggerCensusRiskAssessment = () => {
  const queryClient = useQueryClient()
  return useMutation(quoteService.triggerCensusRiskAssessment, {
    onSuccess: () => {
      queryClient.invalidateQueries(EQueryKeys.censusRisk)
      queryClient.removeQueries(EQueryKeys.censusRisk)
    }
  })
}

export const usePollForCensusRiskAssessment = (
  quoteId: number | string | null
) =>
  useQuery(
    [EQueryKeys.censusRisk, quoteId],
    () =>
      quoteId
        ? quoteService.getCensusRiskAssessment(quoteId?.toString())
        : undefined,
    {
      enabled: !!quoteId,
      refetchInterval: (data) => (data?.stillProcessing ? 10000 : false)
    }
  )

export const useGroups = (params?: IGroupQueryParams) =>
  useQuery(EQueryKeys.quoteGroups, () =>
    params ? quoteService.getGroups(params) : quoteService.getGroups()
  )

export const useCreateGroup = () => {
  const queryClient = useQueryClient()
  return useMutation(quoteService.createGroup, {
    onSuccess: () => {
      queryClient.invalidateQueries(EQueryKeys.quoteGroups)
      queryClient.refetchQueries(EQueryKeys.quoteGroups)
    }
  })
}

export const useUpdateGroup = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ groupId, data }: { groupId: number; data: Partial<ICreateGroupDTO> }) =>
      quoteService.updateGroup(groupId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EQueryKeys.quoteGroups)
        queryClient.refetchQueries(EQueryKeys.quoteGroups)
      }
    }
  )
}

export const useGroupChoices = () =>
  useQuery([EQueryKeys.quoteGroups, 'choices'], quoteService.getGroupChoices)

export const useSearchGeneralAgencies = (searchTerm: string) =>
  useQuery([EQueryKeys.generalAgencies, searchTerm], () => quoteService.getGeneralAgencies(searchTerm), {
    enabled: Boolean(searchTerm)
  })

export const useVendorChoices = (quoteId: number | undefined | null) =>
  useQuery(
    [EQueryKeys.vendorChoices, quoteId],
    () => (quoteId ? quoteService.getVendorChoices(quoteId) : undefined),
    {
      enabled: !!quoteId
    }
  )

export const useMedicalPlans = (id: number | null) =>
  useQuery(EQueryKeys.medicalPlans, () =>
    id ? quoteService.getMedicalPlans(id) : undefined
  )

export const useCreateMedPlan = () => {
  const queryClient = useQueryClient()
  return useMutation(quoteService.createMedicalPlan, {
    onSuccess: (data) => {
      queryClient.setQueryData<IMedicalPlanDTO[] | undefined>(
        EQueryKeys.medicalPlans,
        (oldData) => {
          if (oldData) {
            return [...oldData, data]
          }
          return oldData
        }
      )
    }
  })
}

export const useEditMedPlan = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({
      entryId,
      data
    }: {
      entryId: number | string
      data: Partial<IMedicalPlanCreateDTO>
    }) => quoteService.editMedicalPlan(entryId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(EQueryKeys.medicalPlans)
      }
    }
  )
}

export const useDeleteMedPlan = () => {
  const queryClient = useQueryClient()
  return useMutation(quoteService.deleteMedicalPlan, {
    onSuccess: (data, entryId) => {
      queryClient.setQueryData<IMedicalPlanDTO[] | undefined>(
        EQueryKeys.medicalPlans,
        (oldData) => {
          if (oldData) {
            return oldData.filter((d) => d.entryId !== entryId)
          }
          return oldData
        }
      )
    }
  })
}

export const usePastMedicalPlans = (quoteId: string | null | undefined) => {
  return useQuery(
    [EQueryKeys.pastMedicalPlans, quoteId],
    () => (quoteId ? quoteService.getPastMedicalPlans(quoteId) : undefined),
    {
      enabled: !!quoteId
    }
  )
}

export const useCreatePastMedicalPlan = () => {
  return useMutation(
    ({
      quoteId,
      data
    }: {
      quoteId: string
      data: IPastMedicalPlanCreateDTO
    }) => {
      return quoteService.createPastMedicalPlan(quoteId, data)
    }
  )
}

export const useUpdatePastMedicalPlan = () => {
  return useMutation(
    ({
      quoteId,
      entryId,
      data
    }: {
      quoteId: string
      entryId: string
      data: IPastMedicalPlanCreateDTO
    }) => {
      return quoteService.updatePastMedicalPlan(quoteId, entryId, data)
    }
  )
}

export const useDeletePastMedicalPlan = () => {
  return useMutation(
    ({ quoteId, entryId }: { quoteId: string; entryId: string }) => {
      return quoteService.deletePastMedicalPlan(quoteId, entryId)
    }
  )
}

export const useGetExperienceFiles = (quoteId: string | undefined) => {
  return useQuery(
    [EQueryKeys.uploadedFiles, 'experience-info', quoteId],
    () => (quoteId ? quoteService.getExperienceFiles(quoteId) : undefined),
    {
      enabled: !!quoteId
    }
  )
}

export const useGetMonthlyInvoice = (quoteId: string | undefined) => {
  return useQuery(
    [EQueryKeys.uploadedFiles, 'monthly-invoice', quoteId],
    () => (quoteId ? quoteService.getMonthlyInvoiceFiles(quoteId) : undefined),
    {
      enabled: !!quoteId
    }
  )
}

export const useUploadFile = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({
      quoteId,
      files,
      fileType
    }: {
      quoteId: number
      files: File[]
      fileType: IFileType
    }) => {
      const formData = new FormData()

      formData.append('fileType', fileType)

      files.forEach((f) => {
        formData.append('files', f, f.name)
      })

      return quoteService.uploadFile(quoteId, formData)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EQueryKeys.uploadedFiles])
      }
    }
  )
}

export const useDeleteUploadedFile = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({
      quoteId,
      fileId
    }: {
      quoteId: number | string
      fileId: number | string
    }) => quoteService.deleteUploadedFile(quoteId, fileId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EQueryKeys.uploadedFiles])
      }
    }
  )
}

export const useMarkQuoteAsSold = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ quoteId, data }: { quoteId: string; data: ISoldQuoteDTO }) =>
      quoteService.markQuoteAsSold(quoteId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EQueryKeys.quotes)
        queryClient.refetchQueries(EQueryKeys.quotes)
        queryClient.invalidateQueries(EQueryKeys.quotes)
        queryClient.refetchQueries(EQueryKeys.quotes)
      }
    }
  )
}

export const useTriggerQuoteRateCalculation = (
  quoteId: string | undefined,
  enabled: boolean
) => {
  const queryClient = useQueryClient()
  const { showError } = useToaster()
  return useQuery(
    [EQueryKeys.triggerRateCalc],
    () => (quoteId ? quoteService.triggerRateCalculation(quoteId) : undefined),
    {
      enabled: !!quoteId && enabled,
      refetchInterval: (data) =>
        data?.status === 'not_started' ? 5000 : false,
      onSuccess: (data) => {
        switch (data?.status) {
          case 'completed':
          case 'in_review':
            queryClient.invalidateQueries([EQueryKeys.quotes, quoteId])
            return
          case 'error':
            showError(
              'There was an error calculating your rates. Please contact support.'
            )
            return
        }
      }
    }
  )
}

export const useDownloadExcelQuote = () =>
  useMutation((quoteId: string) => quoteService.downloadExcelProposal(quoteId))

export const useCloneQuoteToDraft = () =>
  useMutation((quoteId: string) => quoteService.cloneQuoteToDraft(quoteId))
